import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormExpirationGuard } from '../components/FormExpirationGuard';
import { FormHeaderImage, FormHeaderWrapper, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { SubmissionLoadingOverlay } from '../components/SubmissionLoadingOverlay';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { downloadFile } from '../utils/downloadFile';
import { FORM_IDS } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

// Form validation and types
interface FormValues {
  company: string;
  street: string;
  zipCity: string;
  contactPerson: string;
  phone: string;
  email: string;
  webinarDate: string;
  termsConsent: boolean;
  privacyConsent: boolean;
}

const validationSchema = Yup.object({
  company: Yup.string().required('Firma ist erforderlich'),
  street: Yup.string().required('Straße / Hausnummer ist erforderlich'),
  zipCity: Yup.string().required('PLZ / Ort ist erforderlich'),
  contactPerson: Yup.string().required('Ansprechpartner ist erforderlich'),
  phone: Yup.string(),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('E-Mail Adresse ist erforderlich'),
  webinarDate: Yup.string().required('Bitte wählen Sie einen Termin aus'),
  termsConsent: Yup.boolean()
    .required('Bitte stimmen Sie den Bestimmungen zu')
    .oneOf([true], 'Sie müssen den Bestimmungen zustimmen, um das Formular abzusenden'),
  privacyConsent: Yup.boolean()
    .required('Bitte stimmen Sie den Datenschutzbestimmungen zu')
    .oneOf([true], 'Sie müssen den Datenschutzbestimmungen zustimmen, um das Formular abzusenden'),
});

const initialValues: FormValues = {
  company: '',
  street: '',
  zipCity: '',
  contactPerson: '',
  phone: '',
  email: '',
  webinarDate: '',
  termsConsent: false,
  privacyConsent: false,
};

// API endpoint
const API_ENDPOINT = 'https://3579fdf80154ea27b4d1f7c7d4a13a.57.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/6933302a7136460eb88a74fababb04da/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=M-nY4bGRLkqLGb7-M-dewJngnMcrsGjWRcgO0zNOR84';

// Webinar date options
const WEBINAR_DATES = [
  { value: '2026-04-14', label: '14.04.26 14:00 - 15:30 Uhr' },
  { value: '2026-04-17', label: '17.04.26 09:30 - 11:00 Uhr' },
];

export const KiWirdMitarbeiter = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [selectedSession, setSelectedSession] = useState('');
  const headerImage = useHeaderImage(FORM_IDS.KI_WIRD_MITARBEITER);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);
    setSelectedSession(values.webinarDate);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          company: values.company,
          street: values.street,
          zipCity: values.zipCity,
          contactPerson: values.contactPerson,
          phone: values.phone,
          mail: values.email,
          price: 69,
          webinarDate: values.webinarDate,
          webinarTime: values.webinarDate === '2026-04-14' ? '14:00 - 15:30 Uhr' : '09:30 - 11:00 Uhr',
          webinarName: 'KI wird selbstständig. Wie aus Assistenten digitale Mitarbeiter werden - und worauf es jetzt ankommt.',
          termsConsent: values.termsConsent,
          privacyConsent: values.privacyConsent,
          // SharePoint path where the backend retrieves the ICS file for the confirmation email
          relativeFileUrl: values.webinarDate === '2026-04-14'
            ? '/WebinarICSFiles/2026-04%20KI%20wird%20Mitarbeiter/Webinar%20-%20KI%20wird%20selbstst%C3%A4ndig!%202026-04-14.ics'
            : '/WebinarICSFiles/2026-04%20KI%20wird%20Mitarbeiter/Webinar%20-%20KI%20wird%20selbstst%C3%A4ndig!%202026-04-17.ics',
        },
        debugFieldValue: values.contactPerson,
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (!selectedSession) {
      console.warn('No webinar session selected for download');
      return;
    }
    const originalFileName = selectedSession === '2026-04-14' ? 'Webinar 2026-04-14.ics' : 'Webinar 2026-04-17.ics';
    const friendlyFileName = selectedSession === '2026-04-14'
      ? 'KI-wird-Mitarbeiter-14-04-2026.ics'
      : 'KI-wird-Mitarbeiter-17-04-2026.ics';

    downloadFile({
      fileUrl: `${window.location.origin}/forms/kiwirdmitarbeiter/${encodeURIComponent(originalFileName)}`,
      fileName: friendlyFileName,
      mimeType: 'text/calendar',
      redirectUrl: 'https://www.lansco.de/',
    });
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="KI wird selbstständig" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        <FormExpirationGuard
          expirationDate="2026-04-17"
          expirationTime="09:15"
          expiredMessage="Der Anmeldezeitraum für dieses Webinar ist leider abgelaufen. Eine Anmeldung ist nicht mehr möglich."
        >
        {!isSubmitted ? (
          <>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, mb: 3, color: 'primary.main' }}>
              KI wird selbstständig. Wie aus Assistenten digitale Mitarbeiter werden - und worauf es jetzt ankommt.
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              Künstliche Intelligenz entwickelt sich gerade vom Assistenten zum ausführenden System. Statt nur zu antworten, plant, entscheidet und handelt KI zunehmend eigenständig – mit Zugriff auf Werkzeuge, Daten und Arbeitskontexte. Mit Entwicklungen wie Copilot Cowork, Agent Governance in Microsoft 365 und offenen Ansätzen rund um autonome bzw. always-on AI entsteht ein neues Bild: der digitale Mitarbeiter.
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              In diesem 90-minütigen Deep Dive zeigen wir, warum dieser Wandel gerade jetzt Fahrt aufnimmt, welche technischen Prinzipien dahinterstehen, warum Microsoft 365 dafür ein besonders starkes Fundament bietet – und welche Governance-, Sicherheits- und Organisationsfragen Unternehmen jetzt klären müssen.
            </Typography>
            <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Das erwartet Dich:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mb: 2, pl: 2, '& li': { mb: 1 } }}>
              <li>Warum KI gerade vom Antwortsystem zum Handlungssystem wird</li>
              <li>Was hinter Begriffen wie Copilot Cowork, autonome KI, always-on AI und Agentensysteme wirklich steckt</li>
              <li>Wie sich Microsoft-Ansätze und offene Agent-Modelle einordnen lassen</li>
              <li>Welche Governance- und Sicherheitsfragen bei autonomer KI in Unternehmen zwingend geklärt werden müssen</li>
              <li>Eine konkrete Demo, wie digitale Mitarbeiter in der M365-Welt kontrolliert Aufgaben übernehmen können</li>
              <li>Praktische Leitlinien, wie Unternehmen sich agent-ready aufstellen</li>
            </Typography>
            <SubmissionLoadingOverlay open={isLoading} />
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnMount={true}
              initialTouched={{
                termsConsent: true,
                privacyConsent: true,
              }}
            >
              {({ errors, touched, isValid, dirty }) => (
                <Form>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="company"
                    label="Firma"
                    error={touched.company && Boolean(errors.company)}
                    helperText={touched.company && errors.company}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="street"
                    label="Straße / Hausnummer"
                    error={touched.street && Boolean(errors.street)}
                    helperText={touched.street && errors.street}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="zipCity"
                    label="PLZ / Ort"
                    error={touched.zipCity && Boolean(errors.zipCity)}
                    helperText={touched.zipCity && errors.zipCity}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="contactPerson"
                    label="Ansprechpartner"
                    error={touched.contactPerson && Boolean(errors.contactPerson)}
                    helperText={touched.contactPerson && errors.contactPerson}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="phone"
                    label="Telefonnummer"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="email"
                    label="E-Mail Adresse"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    margin="normal"
                    name="webinarDate"
                    label="Terminauswahl"
                    error={touched.webinarDate && Boolean(errors.webinarDate)}
                    helperText={touched.webinarDate && errors.webinarDate}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  >
                    {WEBINAR_DATES.filter((option) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return new Date(option.value) >= today;
                    }).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>

                  <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Registrierungsgebühr pro Person: 69€
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Bitte beachte, dass die genannten Preise sich exkl. der gesetzlich geltenden Mehrwertsteuer verstehen.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Nach Anmeldung über folgendes Formular erhältst Du direkt den Link zum LiveEvent 😊.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Wir freuen uns auf Deine Anmeldung zu unserem Webinar!
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Berücksichtige bitte, dass eine Aufzeichnung der präsentierten Inhalte strengstens untersagt ist. Ergänzend dazu ist es ausdrücklich untersagt, den Einwahllink für das Meeting zu teilen.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Mit der Bestätigung Ihrer Anmeldung erklärst Du Dich mit dem Erhalt einer Rechnung per E-Mail einverstanden.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Im Falle eines Seminarausfalles aufgrund höherer Gewalt oder sonstiger unvorhersehbarer Ereignisse übernehmen wir keine Haftung. Für eventuelle Auslagen aufgrund von Arbeitsausfall, entgangenem Gewinn oder Ansprüche Dritter ist demnach kein Anspruch gültig. Bei einem Seminarausfall wird Dir ein Alternativtermin angeboten oder es werden die Seminargebühren zurückerstattet.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Bei Fragen zu diesem Event oder zur Anmeldung wende Dich gerne an{' '}
                      <a href="mailto:sales@lansco.de">sales@lansco.de</a>. Alternativ steht Dir auch unsere Supporthotline unter{' '}
                      <a href="tel:+4992815469850">+49 9281 5469850</a> zur Verfügung.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Fortführend gelten unsere aktuellen{' '}
                      <a href="https://www.lansco.de/lansco/datenschutz.html" target="_blank" rel="noopener noreferrer">
                        Datenschutzbestimmungen
                      </a> &amp; unser{' '}
                      <a href="https://www.lansco.de/lansco/impressum.html" target="_blank" rel="noopener noreferrer">
                        Impressum
                      </a>
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Field
                      type="checkbox"
                      name="termsConsent"
                    >
                      {({ field, form }: FieldProps<boolean, FormValues>) => (
                        <div>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <input
                              type="checkbox"
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              name={field.name}
                              id="termsConsent-checkbox"
                              checked={field.value}
                              style={{ marginRight: '10px', position: 'relative', top: '2px', flexShrink: 0 }}
                              disabled={isLoading}
                            />
                            <label htmlFor="termsConsent-checkbox">
                              <Typography variant="body2" component="span">
                                <strong>Ich bin mit den Bestimmungen einverstanden und bestelle mit dem Absenden des Formulars kostenpflichtig per Rechnung.</strong>
                              </Typography>
                            </label>
                          </Box>
                          {form.touched.termsConsent && form.errors.termsConsent && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
                              {form.errors.termsConsent}
                            </Typography>
                          )}
                        </div>
                      )}
                    </Field>
                  </Box>

                  <PrivacyConsentSection
                    fieldName="privacyConsent"
                    checkboxId="privacyConsent-checkbox"
                    label="Ja, ich stimme den Datenschutzbestimmungen zu"
                    isLoading={isLoading}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <LanscoButton
                      type="submit"
                      disabled={!(isValid && dirty) || isLoading}
                      sx={{ minWidth: 180 }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Kostenpflichtig anmelden'}
                    </LanscoButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
              Vielen Dank für Deine Anmeldung!
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 4 }}>
              Deine Anmeldung wurde erfolgreich empfangen. Du erhältst in Kürze eine E-Mail mit dem Link zum LiveEvent sowie der Rechnung. Lade Dir jetzt den Kalendertermin herunter, damit Du das Webinar nicht verpasst.
            </Typography>
            <LanscoButton
              // @ts-ignore - Custom variant and HTML anchor props
              variant='white'
              component="a"
              onClick={handleDownload}
              sx={{ minWidth: 220 }}
            >
              Kalendertermin herunterladen
            </LanscoButton>
          </Box>
        )}
      </FormExpirationGuard>
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};
